import React, { useEffect, useRef, useState } from 'react';
import { fetchSocialConfig, loginWithGoogle, loginWithFacebook } from '../lib/portalApi';
import { useT } from '../i18n/LanguageContext';

// Loads a third-party script once (Google Identity Services / Facebook SDK).
const loaded = {};
function loadScript(src) {
  if (loaded[src]) return loaded[src];
  loaded[src] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return loaded[src];
}

// Renders "Continue with Google / Facebook" buttons when the provider IDs are
// configured. On success it persists the session and calls onAuthed().
export default function SocialLogin({ onAuthed }) {
  const t = useT();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const googleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchSocialConfig().then((c) => { if (!cancelled) setConfig(c); });
    return () => { cancelled = true; };
  }, []);

  // Google Identity Services: init + render the official button.
  useEffect(() => {
    if (!config?.google_client_id || !googleRef.current) return;
    let cancelled = false;
    loadScript('https://accounts.google.com/gsi/client')
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: config.google_client_id,
          callback: async ({ credential }) => {
            setError(''); setBusy(true);
            try {
              await loginWithGoogle(credential);
              onAuthed?.();
            } catch (e) {
              setError(e.message || 'No se pudo iniciar sesión con Google.');
            } finally {
              setBusy(false);
            }
          },
        });
        window.google.accounts.id.renderButton(googleRef.current, {
          theme: 'outline', size: 'large', width: 320, text: 'continue_with',
        });
      })
      .catch(() => setError('No se pudo cargar Google.'));
    return () => { cancelled = true; };
  }, [config, onAuthed]);

  const facebookLogin = () => {
    if (!config?.facebook_app_id) return;
    setError(''); setBusy(true);
    loadScript('https://connect.facebook.net/es_ES/sdk.js')
      .then(() => {
        window.FB.init({ appId: config.facebook_app_id, cookie: true, xfbml: false, version: 'v19.0' });
        window.FB.login((resp) => {
          const token = resp.authResponse?.accessToken;
          if (!token) { setBusy(false); return; }
          loginWithFacebook(token)
            .then(() => onAuthed?.())
            .catch((e) => setError(e.message || 'No se pudo iniciar sesión con Facebook.'))
            .finally(() => setBusy(false));
        }, { scope: 'email,public_profile' });
      })
      .catch(() => { setError('No se pudo cargar Facebook.'); setBusy(false); });
  };

  if (!config || (!config.google_client_id && !config.facebook_app_id)) return null;

  return (
    <div className="mt-6">
      <div className="mb-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('auth.orContinue')}</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      {error ? <p className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

      <div className="flex flex-col items-center gap-3">
        {config.google_client_id ? <div ref={googleRef} /> : null}
        {config.facebook_app_id ? (
          <button
            type="button"
            onClick={facebookLogin}
            disabled={busy}
            className="flex w-[320px] max-w-full items-center justify-center gap-2 rounded-lg bg-[#1877F2] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#166fe0] disabled:opacity-60"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
            {t('auth.continueFacebook')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
